/**
 * Pooled particle storage (pure logic, no three.js). Struct-of-arrays in GPU-ready layout so the
 * renderer can hand these Float32Arrays straight to InstancedBufferAttributes:
 *   posAge  x, y, z, t        t = age / life (0..1); >= 1 means dead (the shader collapses it)
 *   vel     vx, vy, vz, k     k = velocity stretch (0 = rotated billboard)
 *   sizeRot size0, size1, rot0, rotVel
 *   colA    r, g, b, alpha0
 *   colB    r, g, b, alpha1
 *   misc    frame, life (s), flags, floorY (floor height under the spawn point)
 * Slots are recycled through a free stack; nothing is allocated after construction.
 * When full, a spawn evicts the oldest particle of a strictly lower priority (or fails).
 */

import { groundY } from '../ground';

export const PF_FLOOR_FADE = 1; // fade out near the floor (soft-particle substitute)
export const PF_BOUNCE = 2; // bounce off the floor instead of sinking
export const PF_GROUND_CLEAR = 4; // friendly: stronger fade near the floor so danger zones stay readable

/** Mutable spawn description. Fill the shared scratch (`particle()`) and pass it to `spawn`. */
export interface ParticleInit {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Seconds. */
  life: number;
  size0: number;
  size1: number;
  rot: number;
  rotVel: number;
  r0: number;
  g0: number;
  b0: number;
  a0: number;
  r1: number;
  g1: number;
  b1: number;
  a1: number;
  /** Downward acceleration (yd/s²); negative floats up. */
  gravity: number;
  /** Exponential velocity damping per second. */
  drag: number;
  /** Atlas frame. */
  frame: number;
  /** Velocity stretch factor (0 = plain rotated billboard). */
  stretch: number;
  flags: number;
  priority: number;
  /** Screen region for overdraw accounting (see budget.ts). */
  region: number;
  /** Overdraw weight per particle (≈ relative screen area). */
  cost: number;
}

export function blankParticle(): ParticleInit {
  return {
    x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, life: 1, size0: 0.3, size1: 0.3, rot: 0, rotVel: 0,
    r0: 1, g0: 1, b0: 1, a0: 1, r1: 1, g1: 1, b1: 1, a1: 0, gravity: 0, drag: 0, frame: 0, stretch: 0,
    flags: 0, priority: 1, region: 0, cost: 1,
  };
}

export class ParticlePool {
  readonly posAge: Float32Array;
  readonly vel: Float32Array;
  readonly sizeRot: Float32Array;
  readonly colA: Float32Array;
  readonly colB: Float32Array;
  readonly misc: Float32Array;
  /** Live overdraw cost per screen region. */
  readonly regionLoad: Float32Array;

  private readonly age: Float32Array;
  private readonly life: Float32Array;
  private readonly gravity: Float32Array;
  private readonly drag: Float32Array;
  private readonly prio: Uint8Array;
  private readonly region: Uint8Array;
  private readonly cost: Float32Array;
  private readonly alive: Uint8Array;
  private readonly free: Int32Array;
  private freeTop: number;
  /** One past the highest slot ever handed out (instanceCount for the GPU). */
  hwm = 0;
  liveCount = 0;
  /** Set when static attributes (size/colour/misc) changed since the last `consumeDirty`. */
  dirty = false;

  constructor(readonly capacity: number, regions = 1) {
    this.posAge = new Float32Array(capacity * 4);
    this.vel = new Float32Array(capacity * 4);
    this.sizeRot = new Float32Array(capacity * 4);
    this.colA = new Float32Array(capacity * 4);
    this.colB = new Float32Array(capacity * 4);
    this.misc = new Float32Array(capacity * 4);
    this.regionLoad = new Float32Array(Math.max(1, regions));
    this.age = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.gravity = new Float32Array(capacity);
    this.drag = new Float32Array(capacity);
    this.prio = new Uint8Array(capacity);
    this.region = new Uint8Array(capacity);
    this.cost = new Float32Array(capacity);
    this.alive = new Uint8Array(capacity);
    this.free = new Int32Array(capacity);
    for (let i = 0; i < capacity; i++) {
      this.free[i] = capacity - 1 - i; // pop order 0, 1, 2…
      this.posAge[i * 4 + 3] = 2;
    }
    this.freeTop = capacity;
  }

  get freeCount(): number {
    return this.freeTop;
  }

  isAlive(i: number): boolean {
    return this.alive[i] === 1;
  }

  priorityOf(i: number): number {
    return this.prio[i] ?? 0;
  }

  /** Returns the slot index, or -1 when full and nothing of lower priority can be evicted. */
  spawn(p: ParticleInit): number {
    let i: number;
    if (this.freeTop > 0) i = this.free[--this.freeTop]!;
    else {
      i = this.findVictim(p.priority);
      if (i < 0) return -1;
      this.release(i, false);
    }
    this.alive[i] = 1;
    this.liveCount++;
    if (i + 1 > this.hwm) this.hwm = i + 1;
    const o = i * 4;
    const life = Math.max(0.016, p.life);
    this.posAge[o] = p.x;
    this.posAge[o + 1] = p.y;
    this.posAge[o + 2] = p.z;
    this.posAge[o + 3] = 0;
    this.vel[o] = p.vx;
    this.vel[o + 1] = p.vy;
    this.vel[o + 2] = p.vz;
    this.vel[o + 3] = p.stretch;
    this.sizeRot[o] = p.size0;
    this.sizeRot[o + 1] = p.size1;
    this.sizeRot[o + 2] = p.rot;
    this.sizeRot[o + 3] = p.rotVel;
    this.colA[o] = p.r0;
    this.colA[o + 1] = p.g0;
    this.colA[o + 2] = p.b0;
    this.colA[o + 3] = p.a0;
    this.colB[o] = p.r1;
    this.colB[o + 1] = p.g1;
    this.colB[o + 2] = p.b1;
    this.colB[o + 3] = p.a1;
    this.misc[o] = p.frame;
    this.misc[o + 1] = life;
    this.misc[o + 2] = p.flags;
    // w: floor height under the spawn point (elevation): bounce / floor fade are relative to it.
    this.misc[o + 3] = groundY(p.x, -p.z);
    this.age[i] = 0;
    this.life[i] = life;
    this.gravity[i] = p.gravity;
    this.drag[i] = p.drag;
    this.prio[i] = Math.max(0, Math.min(255, p.priority | 0));
    const r = Math.max(0, Math.min(this.regionLoad.length - 1, p.region | 0));
    this.region[i] = r;
    this.cost[i] = p.cost;
    this.regionLoad[r]! += p.cost;
    this.dirty = true;
    return i;
  }

  /** Oldest particle with priority strictly below `priority` (lowest priority first). */
  private findVictim(priority: number): number {
    let best = -1;
    let bestPrio = priority;
    let bestT = -1;
    for (let i = 0; i < this.hwm; i++) {
      if (!this.alive[i]) continue;
      const pr = this.prio[i]!;
      const t = this.posAge[i * 4 + 3]!;
      if (pr < bestPrio || (pr === bestPrio && best >= 0 && t > bestT)) {
        best = i;
        bestPrio = pr;
        bestT = t;
      }
    }
    return best;
  }

  private release(i: number, pushFree: boolean): void {
    if (!this.alive[i]) return;
    this.alive[i] = 0;
    this.liveCount--;
    this.posAge[i * 4 + 3] = 2;
    const r = this.region[i]!;
    this.regionLoad[r] = Math.max(0, this.regionLoad[r]! - this.cost[i]!);
    if (pushFree) this.free[this.freeTop++] = i;
  }

  kill(i: number): void {
    if (i < 0 || i >= this.capacity || !this.alive[i]) return;
    this.release(i, true);
  }

  /** Kill every live particle below `priority` (budget emergency valve). Returns how many died. */
  killBelow(priority: number): number {
    let n = 0;
    for (let i = 0; i < this.hwm; i++) {
      if (this.alive[i] && this.prio[i]! < priority) {
        this.release(i, true);
        n++;
      }
    }
    return n;
  }

  /** Integrate and expire. `dt` in seconds. */
  update(dt: number): void {
    if (dt <= 0) return;
    let top = 0;
    for (let i = 0; i < this.hwm; i++) {
      if (!this.alive[i]) continue;
      const a = this.age[i]! + dt;
      const life = this.life[i]!;
      if (a >= life) {
        this.release(i, true);
        continue;
      }
      this.age[i] = a;
      const o = i * 4;
      const d = this.drag[i]!;
      const damp = d > 0 ? 1 / (1 + d * dt) : 1;
      let vx = this.vel[o]! * damp;
      let vy = (this.vel[o + 1]! - this.gravity[i]! * dt) * damp;
      let vz = this.vel[o + 2]! * damp;
      let y = this.posAge[o + 1]! + vy * dt;
      const floorY = this.misc[o + 3]!;
      if (y < floorY + 0.03 && vy < 0 && (this.misc[o + 2]! & PF_BOUNCE) !== 0) {
        y = floorY + 0.03;
        vy = -vy * 0.3;
        vx *= 0.55;
        vz *= 0.55;
      }
      this.vel[o] = vx;
      this.vel[o + 1] = vy;
      this.vel[o + 2] = vz;
      this.posAge[o] = this.posAge[o]! + vx * dt;
      this.posAge[o + 1] = y;
      this.posAge[o + 2] = this.posAge[o + 2]! + vz * dt;
      this.posAge[o + 3] = a / life;
      top = i + 1;
    }
    // Trim the high-water mark only when the free stack can still hand out every slot above it.
    if (top < this.hwm) this.hwm = top;
  }

  consumeDirty(): boolean {
    const d = this.dirty;
    this.dirty = false;
    return d;
  }

  clear(): void {
    for (let i = 0; i < this.hwm; i++) if (this.alive[i]) this.release(i, true);
    this.hwm = 0;
    this.regionLoad.fill(0);
  }
}
